import { useState, useEffect } from "react";
import FormSelect from "../../../../../components/Form/FormSelect";
import CheckboxField from "../../../../../components/Form/CheckboxField";
import PaymentMethodFields from "./PaymentMethodFields";
import FormTextarea from "../../../../../components/Form/TextArea";

const methodOptions = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "paypal", label: "PayPal" },
  { value: "payoneer", label: "Payoneer" },
  { value: "skrill", label: "Skrill" },
  { value: "other", label: "Other" },
];

const INITIAL_STATE = {
  payment_method: "",
  note: "",
  active_flag: true,
  is_default: false,
  payment_method_name: "",
  beneficiary_name: "",
  beneficiary_address: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  swift: "",
  iban: "",
  sort_code: "",
  bank_address: "",
  country: "",
  state_region: "",
  city: "",
  postal_code: "",
  email: "",
};

const PaymentMethodForm = ({ methodToEdit, onSave, onCancel }) => {
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (methodToEdit) {
      let detailsToFlatten = {};

      if (
        methodToEdit.payment_method === "bank_transfer" &&
        methodToEdit.bank_transfer_detail
      ) {
        detailsToFlatten = methodToEdit.bank_transfer_detail;
      } else if (
        ["paypal", "payoneer", "skrill"].includes(
          methodToEdit.payment_method
        ) &&
        methodToEdit.email_payment_detail
      ) {
        detailsToFlatten = methodToEdit.email_payment_detail;
      } else if (
        methodToEdit.payment_method === "other" &&
        methodToEdit.other_payment_detail
      ) {
        detailsToFlatten = methodToEdit.other_payment_detail;
      }

      const flattenedData = {
        ...INITIAL_STATE,
        ...methodToEdit,
        ...detailsToFlatten,
      };

      setFormData(flattenedData);
    } else {
      setFormData(INITIAL_STATE);
    }
  }, [methodToEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.payment_method) {
      setError("Please select a Payment Method.");
      return;
    }
    if (formData.payment_method === "bank_transfer" && !formData.bank_name) {
      setError("Bank Name is required for Bank Transfer.");
      return;
    }
    if (
      ["paypal", "payoneer", "skrill"].includes(formData.payment_method) &&
      !formData.email
    ) {
      setError("Email is required for this payment method.");
      return;
    }
    if (formData.payment_method === "other" && !formData.payment_method_name) {
      setError("Payment Method Name is required for 'Other' method.");
      return;
    }

    setError("");
    setSaving(true);

    const payload = {
      payment_method: formData.payment_method,
      note: formData.note,
      active_flag: formData.active_flag,
      is_default: formData.is_default,
      details: {},
    };

    if (formData.payment_method === "bank_transfer") {
      payload.details = {
        payment_method_name: formData.payment_method_name,
        beneficiary_name: formData.beneficiary_name,
        beneficiary_address: formData.beneficiary_address,
        bank_name: formData.bank_name,
        account_number: formData.account_number,
        ifsc_code: formData.ifsc_code,
        swift: formData.swift,
        iban: formData.iban,
        sort_code: formData.sort_code,
        bank_address: formData.bank_address,
        country: formData.country,
        state_region: formData.state_region,
        city: formData.city,
        postal_code: formData.postal_code,
      };
    } else if (
      ["paypal", "payoneer", "skrill"].includes(formData.payment_method)
    ) {
      payload.details = { email: formData.email };
    } else if (formData.payment_method === "other") {
      payload.details = { payment_method_name: formData.payment_method_name };
    }

    try {
      await onSave(payload);
    } catch (error) {
      setError("Failed to save payment method. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900">
          {methodToEdit ? "Edit Payment Method" : "Add Payment Method"}
        </h2>
      </div>
      <form
        onSubmit={handleFormSubmit}
        noValidate
        className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-5"
      >
        <FormSelect
          label="Payment Method"
          name="payment_method"
          value={formData.payment_method}
          onChange={handleChange}
          options={methodOptions}
          required
          disabled={!!methodToEdit}
        />

        {formData.payment_method && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start pt-2 border-t border-gray-100">
            <PaymentMethodFields
              formData={formData}
              handleChange={handleChange}
            />
          </div>
        )}

        <FormTextarea
          label="Note"
          name="note"
          value={formData.note}
          onChange={handleChange}
          placeholder="Add any relevant notes here..."
          rows={3}
        />

        <CheckboxField
          label="Set as active payment method"
          name="active_flag"
          checked={formData.active_flag}
          onChange={handleChange}
        />

        <CheckboxField
          label="Set as default payment method"
          name="is_default"
          checked={formData.is_default}
          onChange={handleChange}
        />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
            {error}
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 space-y-3">
          <span className="block text-xs text-gray-500">
            Fields marked with <span className="text-red-500">*</span> are mandatory.
          </span>
          <div className="flex gap-2 text-sm">
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : methodToEdit ? "Update" : "Save"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PaymentMethodForm;
